import { BroadcastChannel } from 'broadcast-channel';
import { ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react';

// Add any additional message types here.
export type Message = { type: 'logout' } | { type: 'swUpdated' };

export interface IBroadcastChannelContext {
  postMessage: (message: Message) => Promise<void>;
  addEventListener: (handler: (message: Message) => void) => void;
  removeEventListener: (handler: (message: Message) => void) => void;
}

export const BroadcastChannelContext = createContext<IBroadcastChannelContext>({
  postMessage: () => Promise.resolve(),
  addEventListener: () => {},
  removeEventListener: () => {},
});

export interface BroadcastChannelProviderProps {
  children: ReactNode;
}

/** Wrap the app in this provider to allow access to the Ocula app broadcast channel. */
export function BroadcastChannelProvider({ children }: BroadcastChannelProviderProps) {
  const [channel] = useState<BroadcastChannel<Message>>(
    () => new BroadcastChannel('ocula-web-app', { webWorkerSupport: false }),
  );

  const value = useMemo(
    () => ({
      postMessage: (message: Message) => channel.postMessage(message),
      addEventListener: (handler: (message: Message) => void) => channel.addEventListener('message', handler),
      removeEventListener: (handler: (message: Message) => void) => channel.removeEventListener('message', handler),
    }),
    [channel],
  );

  return <BroadcastChannelContext.Provider value={value}>{children}</BroadcastChannelContext.Provider>;
}

/** Returns a function that can be used to send a message to the Ocula app broadcast channel. */
export function useBroadcastChannelSender() {
  return useContext(BroadcastChannelContext).postMessage;
}

/** Registers the given handler so it is invoked when a message is received from the Ocula app broadcast channel.
 *
 * @param handler Must be a stable callback (i.e., memoized).
 */
export function useBroadcastChannelReceiver(handler: (message: Message) => void) {
  const { addEventListener, removeEventListener } = useContext(BroadcastChannelContext);

  useEffect(() => {
    addEventListener(handler);
    return () => removeEventListener(handler);
  }, [handler, addEventListener, removeEventListener]);
}
